import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import DatetimePicker from "../StrategiesDropdowns/DatetimePicker";
import StrategiesChart from "../MMStrategies/StrategiesChart";
import { ChartWrapper } from "../MMStrategies/style";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { PerpDataPnlTable } from "./PerpDataPnlTable";
import { PerpDataDeltaBreakdown } from "./PerpDataDeltaBreakdown";
import { usePerpDataSummary } from "../../hooks/api/mm/usePerpDataSummary";
import { useMMBybitAssets } from "../../utils/asset";
import { useMarkets } from "../../hooks/api/aevo/useMarkets";
import { computePerpAggregatedPositions } from "../../utils/perpAggregatedPositions";
import { formatAmount } from "../../utils/strings";
import { getValueColor } from "../PnL/utils";
import {
  ContentContainer,
  FilterGroup,
  FiltersWrapper,
  HeaderRow,
  PageTitle,
  PnLWrapper,
  StatCard,
  StatCardLabel,
  StatCardValue,
  StatsCardsWrapper,
  TableContainer,
} from "../PnL/style";

interface IPerpPositionsBundle {
  aevoPositions: ReturnType<typeof import("../../utils/positions").aggregatePositions>;
  binancePositions: ReturnType<typeof import("../../utils/positions/binance").standardizeBinancePositions>;
  bybitPositions: ReturnType<typeof import("../../utils/positions/bybit").standardizeBybitPositions>;
  hyperliquidPositions: ReturnType<typeof import("../../utils/positions/hyperliquid").standardizeHyperliquidPositions>;
  lighterMainnetPositions: ReturnType<typeof import("../../utils/positions/lighter").standardizeLighterPositions>;
  lighterPositions: ReturnType<typeof import("../../utils/positions/lighter").standardizeLighterPositions>;
}

interface IPerpDataAssetDetailProps {
  asset: string;
  onBack: () => void;
  perpPositions: IPerpPositionsBundle;
  isPositionsLoading: boolean;
  lighterCoverage: ReactNode;
  positionsComplete: boolean;
}

const getPastDateRange = (days: number): [Date, Date] => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return [start, end];
};

const formatUsd = (value: number) => {
  let prefix = "";
  if (value > 0) prefix = "+";
  else if (value < 0) prefix = "-";
  return `${prefix}$${formatAmount(Math.abs(value))}`;
};

export function PerpDataAssetDetail({
  asset,
  onBack,
  perpPositions,
  isPositionsLoading,
  lighterCoverage,
  positionsComplete,
}: IPerpDataAssetDetailProps) {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getPastDateRange(7));
  const [chartKey, setChartKey] = useState(0);
  const { data: bybitAssets } = useMMBybitAssets();
  const { data: marketsData } = useMarkets();

  const startMs = dateRange[0]?.getTime() ?? 0;
  const endMs = dateRange[1]?.getTime() ?? 0;

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isValidating: isSummaryValidating,
  } = usePerpDataSummary({
    startMs,
    endMs,
    assets: [asset],
    enabled: Boolean(startMs && endMs),
  });

  const pairRow = useMemo(
    () => summary?.pairs.find((row) => row.asset.toUpperCase() === asset.toUpperCase()),
    [summary?.pairs, asset]
  );

  const position = useMemo(() => {
    const positions = computePerpAggregatedPositions(
      perpPositions.aevoPositions,
      perpPositions.binancePositions,
      perpPositions.bybitPositions,
      perpPositions.hyperliquidPositions,
      perpPositions.lighterPositions,
      marketsData,
      perpPositions.lighterMainnetPositions
    );
    return positions.find((row) => row.asset.toUpperCase() === asset.toUpperCase());
  }, [perpPositions, marketsData, asset]);

  const handleDateChange = (startTimestamp: number, endTimestamp: number) => {
    if (!startTimestamp || !endTimestamp) return;
    setDateRange([new Date(startTimestamp * 1000), new Date(endTimestamp * 1000)]);
    setChartKey((key) => key + 1);
  };

  return (
    <PnLWrapper>
      <ContentContainer>
        <HeaderRow>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button buttonTheme={ButtonThemeEnum.NEUTRAL2} onClick={onBack}>
              ← All assets
            </Button>
            <PageTitle>{asset} · Perp</PageTitle>
          </div>
        </HeaderRow>

        <FiltersWrapper>
          <FilterGroup>
            <span>Period</span>
            <DatetimePicker
              onDateChange={handleDateChange}
              overridePlaceholder="Select date range"
              initialDateRange={dateRange}
            />
          </FilterGroup>
        </FiltersWrapper>

        {summary && pairRow && (
          <StatsCardsWrapper>
            <StatCard>
              <StatCardLabel>Net P&L · {summary.period.label}</StatCardLabel>
              <StatCardValue style={{ color: getValueColor(pairRow.combined) }}>
                {formatUsd(pairRow.combined)}
              </StatCardValue>
            </StatCard>
            <StatCard>
              <StatCardLabel>Realized PnL</StatCardLabel>
              <StatCardValue style={{ color: getValueColor(pairRow.realizedPnl) }}>
                {formatUsd(pairRow.realizedPnl)}
              </StatCardValue>
            </StatCard>
            <StatCard>
              <StatCardLabel>Fees</StatCardLabel>
              <StatCardValue style={{ color: getValueColor(-pairRow.fees) }}>
                {formatUsd(-pairRow.fees)}
              </StatCardValue>
            </StatCard>
            <StatCard>
              <StatCardLabel>Net Funding</StatCardLabel>
              <StatCardValue style={{ color: getValueColor(pairRow.netFunding) }}>
                {formatUsd(pairRow.netFunding)}
              </StatCardValue>
            </StatCard>
          </StatsCardsWrapper>
        )}

        <TableContainer style={{ overflow: "visible" }}>
          <h3 style={{ margin: "0 0 12px" }}>Delta breakdown</h3>
          {lighterCoverage}
          {positionsComplete && <PerpDataDeltaBreakdown position={position} isLoading={isPositionsLoading} />}
        </TableContainer>

        <TableContainer>
          <h3 style={{ margin: "0 0 12px" }}>PnL & funding</h3>
          <PerpDataPnlTable
            rows={pairRow ? [pairRow] : []}
            isLoading={isSummaryLoading || isSummaryValidating}
          />
        </TableContainer>

        {dateRange[0] && dateRange[1] && (
          <ChartWrapper key={`${chartKey}-${asset}`} style={{ width: "100%", flex: "none" }}>
            <StrategiesChart
              instrumentName={asset}
              startTime={dateRange[0].getTime() / 1000}
              endTime={dateRange[1].getTime() / 1000}
              strategy="PERP MM"
              bybitAssets={bybitAssets}
              showAllTrades
            />
          </ChartWrapper>
        )}

        {summary?.source && (
          <div style={{ marginTop: 12, fontSize: 12, opacity: 0.7 }}>{summary.source}</div>
        )}
      </ContentContainer>
    </PnLWrapper>
  );
}
